import { toast } from 'react-toastify';

import { all, call, put, takeLatest } from 'redux-saga/effects';

import { InstanceLink } from '~/components/Details/InstanceDetails/MenuInstances';
import api from '~/services/api';
import history from '~/services/history';

import {
  submitModalFailure,
  submitModalRequest,
  submitModalSuccess,
} from '../modal/actions';
import {
  getProcessesSuccess,
  getProcessesFailure,
  getProcessesSolicitationsSuccess,
  getProcessesSolicitationsFailure,
  getProcessesSolicitationsCountSuccess,
  getProcessesSolicitationsCountFailure,
  getProcessesExecutionsSuccess,
  getProcessesExecutionsFailure,
  getProcessesExecutionsCountRequest,
  getProcessesExecutionsCountSuccess,
  getProcessesExecutionsCountFailure,
  getProcessInstanceSuccess,
  getProcessInstanceFailure,
  instantiateProcessSuccess,
  instantiateProcessFailure,
  getProcessXMLSuccess,
  getProcessXMLFailure,
  getProcessFormSuccess,
  getProcessFormFailure,
  cancelProcessSolicitationsSuccess,
  cancelProcessSolicitationsFailure,
  cancelProcessExecutionsSuccess,
  cancelProcessExecutionsFailure,
  getProcessesCountByStatusFailure,
  getProcessesCountByStatusSuccess,
  getAvailableAndFavoriteProcessesSuccess,
  getAvailableAndFavoriteProcessesFailure,
  getProcessSuccess,
  getProcessFailure,
  createFavoriteProcessSuccess,
  createFavoriteProcessFailure,
  deleteFavoriteProcessSuccess,
  deleteFavoriteProcessFailure,
  getExecutionListBySolicitationSuccess,
} from './actions';
import {
  IProcessTypes,
  IInstantiateProcessRequestAction,
  IGetProcessXMLRequestAction,
  IGetProcessFormRequestAction,
  IGetProcessesSolicitationsRequestAction,
  IGetProcessesSolicitationsCountRequestAction,
  IGetProcessesExecutionsRequestAction,
  IGetProcessesExecutionsCountRequestAction,
  IGetProcessInstanceRequestAction,
  ICancelProcessSolicitationsRequestAction,
  ICancelProcessExecutionsRequestAction,
  IGetProcessesCountByStatusRequestAction,
  IGetAvailableAndFavoriteProcessesRequestAction,
  IGetProcessRequestAction,
  ICreateFavoriteProcessRequestAction,
  IDeleteFavoriteProcessRequestAction,
  IGetExecutionListBySolicitationRequestAction,
} from './types';

export function* getProcess({ payload }: IGetProcessRequestAction): any {
  const { key, tenantId } = payload;
  try {
    const response = yield call(api.get, `/processes/${key}`, {
      params: {
        tenant_id: tenantId,
      },
    });

    yield put(getProcessSuccess(response.data));
  } catch (err) {
    yield put(getProcessFailure());
    toast.error('Erro ao tentar buscar solicitação ativa.');
  }
}

export function* getProcesses(): any {
  try {
    const response = yield call(api.get, '/processes');

    yield put(getProcessesSuccess(response.data));
  } catch (err) {
    yield put(getProcessesFailure());
    toast.error('Erro ao tentar listar solicitações ativas.');
  }
}

export function* getAvailableAndFavoriteProcesses({
  payload,
}: IGetAvailableAndFavoriteProcessesRequestAction): any {
  const { personId } = payload;

  try {
    const response = yield call(api.get, '/processes-favorites', {
      params: { personId },
    });

    const { availableProcesses, favoriteProcesses } = response.data;

    yield put(
      getAvailableAndFavoriteProcessesSuccess(
        availableProcesses,
        favoriteProcesses,
      ),
    );
  } catch (err) {
    yield put(getAvailableAndFavoriteProcessesFailure());
    toast.error('Erro ao tentar listar solicitações ativas.');
  }
}

export function* favoriteProcess({
  payload,
}: ICreateFavoriteProcessRequestAction): any {
  const { personId, processDefinitionKey, orgId } = payload;

  try {
    yield call(api.post, '/favorites/create', {
      personId,
      processDefinitionKey,
      orgId,
    });

    yield put(createFavoriteProcessSuccess());
  } catch (err) {
    yield put(createFavoriteProcessFailure(processDefinitionKey));
    toast.error('Erro ao tentar favoritar.');
  }
}

export function* removeFavoriteProcess({
  payload,
}: IDeleteFavoriteProcessRequestAction): any {
  const { personId, processDefinitionKey } = payload;

  try {
    yield call(api.delete, `/favorites/${personId}/${processDefinitionKey}`);

    yield put(deleteFavoriteProcessSuccess());
  } catch (err) {
    yield put(deleteFavoriteProcessFailure(processDefinitionKey));
    toast.error('Erro ao tentar desfavoritar.');
  }
}

export function* getProcessesSolicitations({
  payload,
}: IGetProcessesSolicitationsRequestAction): any {
  const { params } = payload;

  const { processesType } = params;

  try {
    params.active = true;
    const response = yield call(api.get, '/processes-instance', {
      params,
    });

    const { processes, total } = response.data;

    yield put(
      getProcessesSolicitationsSuccess(processes, processesType, total),
    );
  } catch (err) {
    yield put(getProcessesSolicitationsFailure());
    toast.error('Erro ao tentar listar solicitações.');
  }
}

export function* getProcessesSolicitationsCount({
  payload,
}: IGetProcessesSolicitationsCountRequestAction): any {
  const { params } = payload;

  try {
    const response = yield call(api.get, '/processes-history/count', {
      params,
    });

    const { totalInProgress, totalCompleted, totalTerminated } = response.data;

    yield put(
      getProcessesSolicitationsCountSuccess(
        totalInProgress,
        totalCompleted,
        totalTerminated,
      ),
    );
  } catch (err) {
    yield put(getProcessesSolicitationsCountFailure());
    toast.error('Erro ao tentar contar solicitações.');
  }
}

export function* getProcessesExecutions({
  payload,
}: IGetProcessesExecutionsRequestAction): unknown {
  const { params } = payload;

  const { processesType } = params;

  try {
    const response = yield call(api.get, '/processes-instance/executions', {
      params,
    });

    const { processes, total } = response.data;

    yield put(getProcessesExecutionsSuccess(processes, processesType, total));
  } catch (err) {
    yield put(getProcessesExecutionsFailure());
    toast.error('Erro ao tentar listar execuções.');
  }
}

export function* getProcessesExecutionsCount({
  payload,
}: IGetProcessesExecutionsCountRequestAction): unknown {
  const { params } = payload;

  try {
    const response = yield call(
      api.get,
      '/processes-instance/executions/count',
      {
        params,
      },
    );

    const {
      processesExecutionsCompletedTotal,
      processesExecutionsInProgressTotal,
      processesExecutionsTerminatedTotal,
      processesExecutionsTotal,
    } = response.data;

    yield put(
      getProcessesExecutionsCountSuccess(
        processesExecutionsInProgressTotal,
        processesExecutionsCompletedTotal,
        processesExecutionsTerminatedTotal,
        processesExecutionsTotal,
      ),
    );
  } catch (err) {
    yield put(getProcessesExecutionsCountFailure());
    toast.error('Erro ao tentar contar solicitações em andamento.');
  }
}

export function* getProcessesCountByStatus({
  payload,
}: IGetProcessesCountByStatusRequestAction): any {
  try {
    const { params } = payload;
    const response = yield call(
      api.get,
      `/processes-instance/count-by-status`,
      { params },
    );

    const { processesHistoryByStatus, total } = response.data;

    yield put(
      getProcessesCountByStatusSuccess(processesHistoryByStatus, total),
    );
  } catch (err) {
    yield put(getProcessesCountByStatusFailure());
    toast.error('Erro ao tentar contar execuções.');
  }
}

export function* getProcessInstance({
  payload,
}: IGetProcessInstanceRequestAction): any {
  const { id, objectType } = payload;

  try {
    const response = yield call(
      api.get,
      `/processes-instance/${objectType}/${id}`,
    );

    const process = response.data;

    yield put(getProcessInstanceSuccess(process, objectType));
  } catch (err) {
    yield put(getProcessInstanceFailure(objectType));
    toast.error('Erro ao tentar listar detalhes da solicitação.');
  }
}

export function* instantiateProcess({
  payload,
}: IInstantiateProcessRequestAction): any {
  try {
    yield put(submitModalRequest());
    const { process, variables, processFiles } = payload;
    const { key, tenantId: tenant_id } = process;

    const response = yield call(
      api.post,
      '/processes',
      {
        businessKey: key,
        variables,
      },
      {
        params: {
          key,
          tenant_id,
        },
      },
    );

    const processInstanceId = response.data.id;

    const processFilesWithProcessInstanceId = processFiles.map((file) => ({
      ...file,
      process_instance_id: processInstanceId,
      root_process_instance_id: processInstanceId,
    }));

    yield call(api.post, 'processFiles', {
      processFiles: processFilesWithProcessInstanceId,
    });

    yield put(instantiateProcessSuccess());
    yield put(getProcessesExecutionsCountRequest({ limit: 5 }));
    history.push('/processes');
    yield put(submitModalSuccess());
  } catch (err) {
    yield put(instantiateProcessFailure());
    yield put(submitModalFailure());
  }
}

export function* getProcessXML({ payload }: IGetProcessXMLRequestAction): any {
  try {
    const { key, tenant_id } = payload;

    const response = yield call(api.get, `/processes/${key}`, {
      params: {
        key,
        tenant_id,
      },
    });

    yield put(getProcessXMLSuccess(response.data));
  } catch (err) {
    yield put(getProcessXMLFailure());
    toast.error('Falha ao buscar o fluxo do processo.');
  }
}

export function* getProcessForm({
  payload,
}: IGetProcessFormRequestAction): any {
  try {
    const { process } = payload;

    const { id, key, tenantId: tenant_id } = process;

    const response = yield call(api.get, '/processes-form', {
      params: {
        key,
        tenant_id,
        processDefinitionId: id,
      },
    });

    const form = response.data;

    if (form.suspended) {
      yield put(getProcessFormFailure(id));

      toast.error(
        'Este processo está bloqueado temporariamente e não pode receber solicitações. Em caso de dúvidas contate seu gestor ou o suporte do Wedo.',
      );
    } else {
      yield put(getProcessFormSuccess(id, form));
    }
  } catch (err) {
    yield put(getProcessFormFailure('err'));
    toast.error(
      'Falha ao buscar o formulário da solicitação. Contate o suporte.',
    );
    history.push('/requests');
  }
}

export function* cancelProcessSolicitations({
  payload,
}: ICancelProcessSolicitationsRequestAction) {
  const { processInstanceId, processDetails, data } = payload;

  Object.assign(data, {
    ids: [processInstanceId],
    processDetails,
    variables: {
      canceladoPorId: data.canceladoPorId,
      canceladoPor: data.canceladoPor,
      wedo_justificativaDoCancelamento: data.wedo_justificativaDoCancelamento,
    },
    isSolicitation: true,
  });

  try {
    yield call(api.post, `/processes-instance/cancel`, data);

    yield put(cancelProcessSolicitationsSuccess(processInstanceId));
    yield put(getProcessesExecutionsCountRequest({ limit: 5 }));
    toast.success('Solicitação cancelada com sucesso.');
  } catch (err) {
    yield put(cancelProcessSolicitationsFailure());
    toast.error((err as any).response.data.message);
  }
}

export function* cancelProcessExecutions({
  payload,
}: ICancelProcessExecutionsRequestAction) {
  const { processInstanceId, data } = payload;

  Object.assign(data, {
    ids: [processInstanceId],
    variables: {
      canceladoPorId: data.canceladoPorId,
      canceladoPor: data.canceladoPor,
      wedo_justificativaDoCancelamento: data.wedo_justificativaDoCancelamento,
    },
    isSolicitation: false,
  });

  try {
    yield call(api.post, `/processes-instance/cancel`, data);

    yield put(cancelProcessExecutionsSuccess(processInstanceId));
    toast.success('Execução cancelada com sucesso.');
  } catch (err) {
    yield put(cancelProcessExecutionsFailure());
    toast.error('Erro ao cancelar a execução.');
  }
}

export function* getExecutionListBySolicitation({
  payload,
}: IGetExecutionListBySolicitationRequestAction): any {
  const { id } = payload;

  try {
    const response = yield call(
      api.get,
      `/processes-instance/solicitation/${id}/executions`,
    );
    const executions = response.data as Array<InstanceLink>;

    yield put(getExecutionListBySolicitationSuccess(executions));
  } catch (err) {
    yield put(cancelProcessExecutionsFailure());
    toast.error('Erro ao buscar lista de execuções');
  }
}

export default all([
  takeLatest(IProcessTypes.GET_PROCESSES_REQUEST, getProcesses),
  takeLatest(IProcessTypes.GET_PROCESS_REQUEST, getProcess),
  takeLatest(
    IProcessTypes.GET_AVAILABLE_AND_FAVORITE_PROCESSES_REQUEST,
    getAvailableAndFavoriteProcesses,
  ),
  takeLatest(
    IProcessTypes.GET_PROCESSES_SOLICITATIONS_REQUEST,
    getProcessesSolicitations,
  ),
  takeLatest(IProcessTypes.CREATE_FAVORITE_PROCESS_REQUEST, favoriteProcess),
  takeLatest(
    IProcessTypes.DELETE_FAVORITE_PROCESS_REQUEST,
    removeFavoriteProcess,
  ),

  takeLatest(
    IProcessTypes.GET_PROCESSES_SOLICITATIONS_COUNT_REQUEST,
    getProcessesSolicitationsCount,
  ),
  takeLatest(
    IProcessTypes.GET_PROCESSES_EXECUTIONS_REQUEST,
    getProcessesExecutions,
  ),
  takeLatest(
    IProcessTypes.GET_PROCESSES_EXECUTIONS_COUNT_REQUEST,
    getProcessesExecutionsCount,
  ),
  takeLatest(IProcessTypes.GET_PROCESS_INSTANCE_REQUEST, getProcessInstance),
  takeLatest(IProcessTypes.INSTANTIATE_PROCESS_REQUEST, instantiateProcess),
  takeLatest(IProcessTypes.GET_PROCESS_XML_REQUEST, getProcessXML),
  takeLatest(IProcessTypes.GET_PROCESS_FORM_REQUEST, getProcessForm),
  takeLatest(
    IProcessTypes.CANCEL_PROCESS_SOLICITATIONS_REQUEST,
    cancelProcessSolicitations,
  ),
  takeLatest(
    IProcessTypes.CANCEL_PROCESS_EXECUTIONS_REQUEST,
    cancelProcessExecutions,
  ),
  takeLatest(
    IProcessTypes.GET_PROCESSES_COUNT_BY_STATUS_REQUEST,
    getProcessesCountByStatus,
  ),
  takeLatest(
    IProcessTypes.GET_EXECUTION_LIST_BY_SOLICITATION_REQUEST,
    getExecutionListBySolicitation,
  ),
]);
